import { useCallback, useMemo } from "react";
import { useLocation } from "react-router";

import { resolve } from "../Routes/RoutesContext";
import { routesMap } from "../Routes/routesConfig";

// This method allows resolving a child route within the current route context by its
// children routes rootKey.
const findChildRoute = (name, currentRoute) => {
  if (!currentRoute) return null;

  // Index routes can't have direct children, so their children are placed as children
  // of their parent route
  const children = currentRoute.index
    ? currentRoute.parent?.children
    : currentRoute.children;

  let childRoute;

  if (!children) return null;

  children.forEach(child => {
    // If the route is a layout route (it has no path), look for the target route in its
    // children
    if (child.path === void 0) {
      child.children?.forEach(grandChild => {
        if (grandChild.rootKey === name) {
          childRoute = grandChild;
        }
      });
    } else {
      if (child.rootKey === name) {
        childRoute = child;
      }
    }
  });

  // Return the child route if it exists in the routes map, so it has the right nested
  // path.
  return childRoute && routesMap[childRoute.name];
};

// Replace each named parameter in the provided `path` by its value from the input
// `params` object.
const applyParamsTo = (path, params = {}) => {
  return Object.keys(params).reduce(
    (str, key) => str.replace(`:${key}`, params[key]),
    path
  );
};

export function pathFor(name, params = {}, currentRoute = null) {
  // Check whether a child route of the current route exists with the target `name` as its
  // rootKey, allowing therefore to use this route instead of the root one.
  const childRoute = findChildRoute(name, currentRoute);

  // If there's a child route, we need to replicate the current route params, as they'll
  // be needed to resolve the child route, and will not have been provided by the caller
  if (childRoute) {
    params = { ...currentRoute.params, ...params };
  }

  const matchedRoute = childRoute || routesMap[name];

  if (!matchedRoute) return;

  return applyParamsTo(matchedRoute.path, params);
}

// This hook allows for using the `pathFor` function within a component, with access to
// the current route context, which allows dynamically matching child routes by their
// rootKey.
export function useContextualPathFor() {
  const currentPath = useLocation()?.pathname || null;
  const currentRoute = useMemo(() => resolve(currentPath), [currentPath]);

  return useCallback(
    (name, params = {}) => pathFor(name, params, currentRoute),
    [currentRoute]
  );
}
